// ================================================
// EXECUTIVE BRIEFING GENERATOR PANEL
// Feature 1 - Default panel, AI-generated daily briefing
// ================================================
import React, { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, Download, Send, Copy, Check, AlertTriangle,
  TrendingUp, TrendingDown, Calendar, Target, Award,
  AlertCircle, Users, Zap, Clock
} from 'lucide-react';
import { CHIEF_OF_STAFF_COLORS } from '../focus';
import { 
  executiveReviews, 
  executiveSchedule, 
  organizationMetrics,
  awarenessItems,
  winsToAcknowledge,
  formatDate
} from '../../data/chiefOfStaffData';

const ExecutiveBriefingPanel = () => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [briefingData, setBriefingData] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(new Date());

  // Generate the briefing content
  const generateBriefing = useCallback(() => {
    setIsRegenerating(true);
    
    // Simulate AI generation delay
    setTimeout(() => {
      const criticalDecisions = executiveReviews.filter(r => r.hoursPending > 24 || r.slaStatus === 'warning' || r.slaStatus === 'at_risk');
      
      const briefing = {
        date: formatDate(new Date()),
        executiveSummary: `Today the organization is tracking at ${organizationMetrics.company.executionRate}% on-time execution across active projects. ${criticalDecisions.length} items require executive decision before end of day. Soul Food's partnership negotiation with Nike has advanced to the term sheet stage and requires your review.`,
        criticalDecisions: criticalDecisions,
        metrics: {
          executionRate: organizationMetrics.company.executionRate,
          activeProjects: organizationMetrics.company.activeProjects,
          atCapacity: organizationMetrics.company.teamMembersAtCapacity,
          burnoutAlerts: organizationMetrics.company.burnoutRiskAlerts,
          soulFoodMomentum: organizationMetrics.soulFood.momentumScore,
          decisionsThisWeek: organizationMetrics.company.decisionsThisWeek
        },
        schedule: executiveSchedule,
        awarenessItems: awarenessItems.slice(0, 5),
        wins: winsToAcknowledge
      };
      
      setBriefingData(briefing);
      setLastGenerated(new Date());
      setIsRegenerating(false);
    }, 1500);
  }, []);

  useEffect(() => {
    generateBriefing();
  }, [generateBriefing]);

  const handleCopy = () => {
    if (!briefingData) return;
    
    const text = `
EXECUTIVE DAILY BRIEF
${briefingData.date}
Prepared by Chief of Staff

EXECUTIVE SUMMARY
${briefingData.executiveSummary}

CRITICAL DECISIONS REQUIRED TODAY
${briefingData.criticalDecisions.map((d, i) => `${i + 1}. ${d.title} - ${d.submitter.name} (${d.hoursPending}h pending) - ${d.decisionNeeded}`).join('\n')}

KEY METRICS
• Organization Execution Rate: ${briefingData.metrics.executionRate}%
• Active Projects: ${briefingData.metrics.activeProjects}
• Team Members At Capacity: ${briefingData.metrics.atCapacity}
• Burnout Risk Alerts: ${briefingData.metrics.burnoutAlerts}
• Soul Food Momentum: ${briefingData.metrics.soulFoodMomentum}
• Decisions This Week: ${briefingData.metrics.decisionsThisWeek}

ITEMS REQUIRING EXECUTIVE AWARENESS
${briefingData.awarenessItems.map(a => `• ${a.text}`).join('\n')}

WINS TO ACKNOWLEDGE
${briefingData.wins.map(w => `• ${w.text}`).join('\n')}
    `.trim();
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPDF = () => {
    // In a real implementation, this would generate a PDF
    alert('PDF export would generate a formatted briefing document');
  };

  const handleSendToExecutive = () => {
    // In a real implementation, this would open email composition
    alert('This would open a pre-composed email with the briefing content');
  };

  if (!briefingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
          <span className="text-sm text-gray-400">Generating briefing...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-6">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={generateBriefing}
            disabled={isRegenerating}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
            style={{ 
              backgroundColor: CHIEF_OF_STAFF_COLORS.primary,
              color: 'white',
              opacity: isRegenerating ? 0.7 : 1
            }}
          >
            <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all bg-white/10 text-white hover:bg-white/20"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all bg-white/10 text-white hover:bg-white/20"
          >
            <Download className="w-4 h-4" />
            PDF
          </button>
          <button
            onClick={handleSendToExecutive}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
            style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primaryDark, color: 'white' }}
          >
            <Send className="w-4 h-4" />
            Send
          </button>
        </div>
      </div>

      {/* Generated timestamp */}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Clock className="w-3 h-3" />
        Generated {lastGenerated.toLocaleTimeString()}
      </div>

      {/* Briefing Document */}
      <div 
        className="rounded-xl p-6 space-y-6"
        style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
      >
        {/* Header */}
        <div className="text-center border-b border-white/10 pb-4">
          <h1 className="text-lg font-bold text-white mb-1">EXECUTIVE DAILY BRIEF</h1>
          <p className="text-sm" style={{ color: CHIEF_OF_STAFF_COLORS.primaryLight }}>
            {briefingData.date}
          </p>
          <p className="text-xs text-gray-500 mt-1">Prepared by Chief of Staff</p>
        </div>

        {/* Executive Summary */}
        <div className="space-y-2">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Target className="w-4 h-4" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
            EXECUTIVE SUMMARY
          </h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            {briefingData.executiveSummary}
          </p>
        </div>

        {/* Critical Decisions */}
        <div className="space-y-3">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            CRITICAL DECISIONS REQUIRED TODAY
          </h2>
          <div className="space-y-2">
            {briefingData.criticalDecisions.map((decision, idx) => (
              <div 
                key={decision.id}
                className="p-3 rounded-lg"
                style={{ backgroundColor: '#0D1117' }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold" style={{ color: CHIEF_OF_STAFF_COLORS.primary }}>
                        {idx + 1}.
                      </span>
                      <span className="text-sm font-medium text-white">
                        {decision.title}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mb-1">
                      {decision.submitter.name} • {decision.hoursPending}h pending
                    </p>
                    <p className="text-xs text-gray-300">
                      <span className="text-gray-500">Decision needed:</span> {decision.decisionNeeded}
                    </p>
                  </div>
                  <span 
                    className="text-xs font-bold px-2 py-1 rounded"
                    style={{ 
                      backgroundColor: decision.slaStatus === 'at_risk' ? '#EF444420' : '#F59E0B20',
                      color: decision.slaStatus === 'at_risk' ? '#EF4444' : '#F59E0B'
                    }}
                  >
                    {decision.slaStatus === 'at_risk' ? 'URGENT' : 'WARNING'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="space-y-3">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Zap className="w-4 h-4" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
            KEY METRICS SNAPSHOT
          </h2>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Execution Rate', value: `${briefingData.metrics.executionRate}%`, trend: 'up' },
              { label: 'Active Projects', value: briefingData.metrics.activeProjects },
              { label: 'At Capacity', value: briefingData.metrics.atCapacity, alert: true },
              { label: 'Burnout Alerts', value: briefingData.metrics.burnoutAlerts, alert: briefingData.metrics.burnoutAlerts > 0 },
              { label: 'Soul Food Score', value: briefingData.metrics.soulFoodMomentum },
              { label: 'Decisions/Week', value: briefingData.metrics.decisionsThisWeek }
            ].map((metric, idx) => (
              <div 
                key={idx}
                className="p-3 rounded-lg text-center"
                style={{ backgroundColor: '#0D1117' }}
              >
                <p className="text-lg font-bold text-white flex items-center justify-center gap-1">
                  {metric.value}
                  {metric.trend === 'up' && <TrendingUp className="w-3 h-3 text-green-500" />}
                  {metric.alert && <AlertCircle className="w-3 h-3 text-amber-500" />}
                </p>
                <p className="text-xs text-gray-500">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Today's Schedule */}
        <div className="space-y-3">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Calendar className="w-4 h-4" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
            TODAY'S EXECUTIVE SCHEDULE
          </h2>
          <div className="space-y-2">
            {briefingData.schedule.map((item) => (
              <div 
                key={item.id}
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{ backgroundColor: '#0D1117' }}
              >
                <div className="text-right" style={{ width: '70px' }}>
                  <span className="text-sm font-bold" style={{ color: CHIEF_OF_STAFF_COLORS.primary }}>
                    {item.time}
                  </span>
                  <p className="text-xs text-gray-500">{item.duration}</p>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{item.title}</p>
                  <p className="text-xs text-gray-400">{item.prepNote}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Awareness Items */}
        <div className="space-y-3">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <AlertCircle className="w-4 h-4" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
            ITEMS REQUIRING EXECUTIVE AWARENESS
          </h2>
          <ul className="space-y-2">
            {briefingData.awarenessItems.map((item) => (
              <li 
                key={item.id}
                className="flex items-start gap-2 text-sm text-gray-300"
              >
                <span style={{ color: CHIEF_OF_STAFF_COLORS.primary }}>•</span>
                {item.text}
              </li>
            ))}
          </ul>
        </div>

        {/* Wins to Acknowledge */}
        <div className="space-y-3">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Award className="w-4 h-4 text-green-500" />
            WINS TO ACKNOWLEDGE
          </h2>
          <ul className="space-y-2">
            {briefingData.wins.map((win) => (
              <li 
                key={win.id}
                className="flex items-start gap-2 text-sm text-gray-300"
              >
                <span className="text-green-500">✓</span>
                {win.text}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveBriefingPanel;
